import { Avatar, Grid, Typography, Tabs, Tab, Box, Card, CardContent } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Aos from 'aos';
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import {useNavigate, useParams } from 'react-router-dom';

import Navbar from '../../components/CommonComponents/Navbar/Navbar';
import { Gig } from '../../Models/Gig';
import './GigInfoPage.scss';
import pageTextConfig from "../../assets/configs/Gig Info Page/textConfig";

import SimilarGigsSection from '../../components/PageComponents/GigInfoPageComponents/SimilarGigsSection/SimilarGigsSection';
import { GigAPIHandler } from '../../services/NetworkClient/BackendAPIs/GigAPIHandler';
import GGLoaderView from '../../components/UtilityComponents/GGLoaderView/GGLoaderView';
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';
import SnackBar, { SnackTypes } from '../../components/UtilityComponents/SnackBar/SnackBar';
import { SessionManager } from '../../services/SessionManager/SessionManager';
import { BuyerAPIHandler } from '../../services/NetworkClient/BackendAPIs/BuyerAPIHandler';
import GGLoaderButton from '../../components/CommonComponents/GGLoaderButton/GGLoaderButton';
import ImageListModal from '../../components/CommonComponents/ImageListModal/ImageListModal';
import GigReviewsSection from '../../components/PageComponents/GigInfoPageComponents/GigReviewsSection/GigReviewsSection';
import Footer from '../../components/CommonComponents/Footer/Footer';
import ChatIcon from '@mui/icons-material/Chat';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`gig-tabpanel-${index}`}
      aria-labelledby={`gig-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface ThumbnailListProps {
  images: Array<{ url: string }>;
  activeImageUrl: string;
  onImageClick: (imageUrl: string) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}

const ThumbnailList = React.memo(({ images, activeImageUrl, onImageClick, scrollRef, onScrollLeft, onScrollRight }: ThumbnailListProps) => {
  // Preserve scroll position when activeImageUrl changes
  const scrollPositionRef = useRef<number>(0);
  const prevActiveImageUrlRef = useRef<string>(activeImageUrl);
  
  // Save scroll position before activeImageUrl changes
  useLayoutEffect(() => {
    if (scrollRef.current && prevActiveImageUrlRef.current !== activeImageUrl) {
      scrollPositionRef.current = scrollRef.current.scrollLeft;
      prevActiveImageUrlRef.current = activeImageUrl;
    }
  }, [activeImageUrl, scrollRef]);

  // Restore scroll position after activeImageUrl changes (synchronously)
  useLayoutEffect(() => {
    if (scrollRef.current && scrollPositionRef.current > 0) {
      scrollRef.current.scrollLeft = scrollPositionRef.current;
    }
  }, [activeImageUrl, scrollRef]);

  // Update active class when activeImageUrl changes
  useEffect(() => {
    if (!scrollRef.current) return;
    
    const thumbnails = scrollRef.current.querySelectorAll('.thumbnail');
    thumbnails.forEach((thumb) => {
      const imageUrl = thumb.getAttribute('data-image-url');
      if (imageUrl === activeImageUrl) {
        thumb.classList.add('active');
      } else {
        thumb.classList.remove('active');
      }
    });
  }, [activeImageUrl, scrollRef]);

  return (
    <div className="gig-thumbnails-wrapper">
      <div 
        className="thumbnail-arrow-btn left-arrow"
        onClick={onScrollLeft}
        aria-label="Scroll thumbnails left"
      >
        <ChevronLeftIcon />
      </div>
      <div 
        className="gig-thumbnails" 
        ref={scrollRef}
      >
        {images.map((image, idx) => (
          <div
            key={image.url}
            className="thumbnail"
            data-image-url={image.url}
            onClick={() => onImageClick(image.url)}
          >
            <img src={image.url} alt={`gig-thumbnail-${idx}`} />
          </div>
        ))}
      </div>
      <div 
        className="thumbnail-arrow-btn right-arrow"
        onClick={onScrollRight}
        aria-label="Scroll thumbnails right"
      >
        <ChevronRightIcon />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if images array or callbacks change
  // We DO allow re-render when activeImageUrl changes (so useEffect runs to update active class)
  // But we preserve scroll position using refs
  return prevProps.images === nextProps.images && 
         prevProps.onImageClick === nextProps.onImageClick &&
         prevProps.onScrollLeft === nextProps.onScrollLeft &&
         prevProps.onScrollRight === nextProps.onScrollRight &&
         prevProps.activeImageUrl === nextProps.activeImageUrl;
});

ThumbnailList.displayName = 'ThumbnailList';

interface GigImagesSectionProps {
  gig: Gig;
  mainImageURL: string;
  setMainImageURL: (url: string) => void;
  setImageModalIndex: (idx: number) => void;
  setImageModalOpen: (open: boolean) => void;
  thumbnailScrollRef: React.RefObject<HTMLDivElement>;
  handleThumbnailClick: (imageUrl: string) => void;
  handleScrollLeft: () => void;
  handleScrollRight: () => void;
}

const GigImagesSection = React.memo(({ 
  gig, 
  mainImageURL, 
  setMainImageURL, 
  setImageModalIndex, 
  setImageModalOpen,
  thumbnailScrollRef,
  handleThumbnailClick,
  handleScrollLeft,
  handleScrollRight
}: GigImagesSectionProps) => {
  // Memoize the images array to maintain stable reference for ThumbnailList
  // This prevents ThumbnailList from re-rendering when only mainImageURL changes
  const stableImages = useMemo(() => gig?.gigImgs, [gig?.gigImgs]);

  return (
    <div className='gig-image-section'>
      <div className="gig-main-image-container">
        <img
          src={mainImageURL || gig?.gigImgs[0]?.url}
          alt='gig-mainImage'
          className="gig-main-image"
          onClick={() => {
            const currentImageUrl = mainImageURL || gig?.gigImgs[0]?.url;
            const idx = gig?.gigImgs.findIndex(img => img.url === currentImageUrl) ?? 0;
            setImageModalIndex(idx);
            setImageModalOpen(true);
          }}
        />
      </div>
      {stableImages && stableImages.length > 1 && (
        <ThumbnailList
          images={stableImages}
          activeImageUrl={mainImageURL}
          onImageClick={handleThumbnailClick}
          scrollRef={thumbnailScrollRef}
          onScrollLeft={handleScrollLeft}
          onScrollRight={handleScrollRight}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Re-render if:
  // 1. gig object changed (new gig data)
  // 2. callbacks changed (shouldn't happen with useCallback)
  // 3. mainImageURL changed (needed to update main image)
  // Note: Even when mainImageURL changes and GigImagesSection re-renders,
  // ThumbnailList won't re-render because its props (images, callbacks) stay stable
  const gigChanged = prevProps.gig !== nextProps.gig;
  const callbacksChanged = prevProps.handleThumbnailClick !== nextProps.handleThumbnailClick ||
                          prevProps.handleScrollLeft !== nextProps.handleScrollLeft ||
                          prevProps.handleScrollRight !== nextProps.handleScrollRight;
  const mainImageChanged = prevProps.mainImageURL !== nextProps.mainImageURL;
  
  // Return true if props are equal (don't re-render), false if they changed (re-render)
  // We allow mainImageURL changes to trigger re-render for the main image
  return !gigChanged && !callbacksChanged && !mainImageChanged;
});

GigImagesSection.displayName = 'GigImagesSection';

const GigInfoPage = () => {
  const [gig, setGig] = useState<Gig>();
  const { gigId } = useParams();
  const [isGigInFavourites, setIsGigInFavourites] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [addToFavouritesBtnLoader, setAddToFavouritesBtnLoader] = useState(false);
  const [proceedBtnLoader, setProceedBtnLoader] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [mainImageURL, setMainImageURL] = useState<string>('');
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);
  const tabsSectionRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  const handleFavouritesToggle = () => {
    if (!isGigInFavourites) addGigToWishlist()
    else removeGigFromWishlist()
  }

  const addGigToWishlist = () => {
    setAddToFavouritesBtnLoader(true)
    BuyerAPIHandler.shared.addGigToFavourites(gig?.id ?? "").then((response) => {
      SessionManager.shared.buyer = response.buyer
      setAddToFavouritesBtnLoader(false)
      showSnackbar(response.message, SnackTypes.success)
      setIsGigInFavourites(true)
    }).catch(err => {
      setAddToFavouritesBtnLoader(false)
      showSnackbar(err, SnackTypes.error)
    })
  }

  const removeGigFromWishlist = () => {
    setAddToFavouritesBtnLoader(true)
    BuyerAPIHandler.shared.removeGigFromFavourites(gig?.id ?? "").then((response) => {
      SessionManager.shared.buyer = response.buyer
      showSnackbar(response.message, SnackTypes.success)
      setIsGigInFavourites(false)
      setAddToFavouritesBtnLoader(false)
    }).catch(err => {
      setAddToFavouritesBtnLoader(false)
      showSnackbar(err, SnackTypes.error)
    })
  }

  // create chat room
  const createChannel = async (client: any, buyer: any, seller: any, channelId: string) => {
    const channel = await client?.channel(
      'messaging',
      channelId,
      {
        name: `${gig?.seller.fullName}: ${gig?.title}`,
        image: seller.profileImage,
        members: [buyer?.id, seller.buyer],
        ggData: {
          members: [
            {
              id: buyer.id,
              name: buyer.fullName,
              image: buyer.profileImage
            },
            {
              id: seller.buyer,
              name: seller.fullName,
              image: seller.profileImage
            },
          ],
          gigTitle: gig?.title,
        }
      });
    await channel?.create();
    // Navigate to messages page with chat query parameter
    // This will open the chat automatically on both mobile and desktop
    // When user navigates to /my-messages directly (without query param), no chat will be selected
    navigate(`/my-messages?chat=${channelId}`);
  }

  const connectClientUser = async (client: any, buyer: any, seller: any, channelId: string) => {
    // Chat connection is handled by ChatInitializer at app level
    // Just create the channel here
    await createChannel(client, buyer, seller, channelId);
  }

  const createChatRoom = async () => {
    setProceedBtnLoader(true);
    const buyer = SessionManager.shared.buyer;
    const seller = gig?.seller;
    if (buyer && seller && seller.id) {
      if (buyer.seller?.toString() === seller.id.toString()) {
        showSnackbar("Seller can not proceed with owned gigs", SnackTypes.error)
        setProceedBtnLoader(false);
      }
      else {
        // Construct channel ID before creating channel
        const channelId = `${buyer.id}_${gig?.id}`;
        const client = SessionManager.shared.getChatClient();
        await connectClientUser(client, buyer, seller, channelId);
        setProceedBtnLoader(false);
      }
    } else {
      navigate("/login");
      setProceedBtnLoader(false);
    }
  }

  useEffect(() => {
    document.title = "GenieGig | Gig Info";
    Aos.init({ duration: 1000 });
    window.scrollTo(0, 0)
    logAnalyticsEvent(GGAnalyticsEvents.GIG_INFO_PAGE_LOADED);

    setShowLoader(true)
    const fetchGigData = async () => {
      try {
        let gig = await GigAPIHandler.shared.getGigById(gigId as string)
        setGig(gig)
        if (gig.gigImgs && gig.gigImgs.length > 0) {
          setMainImageURL(gig.gigImgs[0].url)
        }
        setShowLoader(false)
        document.title = "GenieGig | " + gig.title;
      } catch (err) {
        setShowLoader(false)
        showSnackbar(err as string, SnackTypes.error)
      }
    }

    const fetchUserData = async () => {
      try {
        let buyer = SessionManager.shared.buyer || await BuyerAPIHandler.shared.getBuyerByAuthToken()
        SessionManager.shared.buyer = buyer
        setIsGigInFavourites(buyer.favourites.find(favrouiteItem => favrouiteItem.gig === gigId) !== undefined)
      } catch (err) {
      }
    }

    fetchGigData()
    fetchUserData()

  }, [gigId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const scrollToPricingTab = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // First, scroll to the tabs section smoothly
    if (tabsSectionRef.current) {
      const tabsSectionTop = tabsSectionRef.current.getBoundingClientRect().top + window.pageYOffset;
      // Account for fixed navbar - adjust offset based on screen size
      let offset = 108; // Default navbar height + padding
      if (window.innerWidth <= 600) {
        offset = 70;
      } else if (window.innerWidth <= 1150) {
        offset = 80;
      } else if (window.innerWidth <= 1440) {
        offset = 82;
      }
      
      window.scrollTo({
        top: tabsSectionTop - offset,
        behavior: 'smooth'
      });
      
      // Switch to Pricing tab after scroll starts (so the tab switch is visible)
      setTimeout(() => {
        setActiveTab(1);
      }, 600); // Delay to allow scroll animation to be visible before tab switch
    }
  };

  // Memoize callbacks to prevent ThumbnailList from re-rendering
  const handleThumbnailClick = useCallback((imageUrl: string) => {
    setMainImageURL(imageUrl);
  }, []);

  const handleScrollLeft = useCallback(() => {
    if (thumbnailScrollRef.current) {
      const scrollAmount = 200;
      const currentScroll = thumbnailScrollRef.current.scrollLeft;
      thumbnailScrollRef.current.scrollTo({
        left: currentScroll - scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (thumbnailScrollRef.current) {
      const scrollAmount = 200;
      const currentScroll = thumbnailScrollRef.current.scrollLeft;
      thumbnailScrollRef.current.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);

  const GigDetailsRightSection = () => {
    // Calculate delivery time range from priceTable
    const getDeliveryTimeRange = () => {
      if (!gig?.priceTable || gig.priceTable.length === 0) return "N/A";
      
      // Convert all durations to days for comparison
      const convertToDays = (duration: number, unit: string): number => {
        switch (unit.toLowerCase()) {
          case 'days':
            return duration;
          case 'weeks':
            return duration * 7;
          case 'months':
            return duration * 30; // Approximate: 1 month = 30 days
          case 'years':
            return duration * 365;
          default:
            return duration; // Default to days if unknown unit
        }
      };

      // Convert all durations to days and find min/max
      const durationsInDays = gig.priceTable.map(p => 
        convertToDays(p.duration, p.durationUnit || 'days')
      );
      const minDays = Math.min(...durationsInDays);
      const maxDays = Math.max(...durationsInDays);

      // Find the original entries for min and max to preserve their units
      const minEntry = gig.priceTable.find(p => 
        convertToDays(p.duration, p.durationUnit || 'days') === minDays
      );
      const maxEntry = gig.priceTable.find(p => 
        convertToDays(p.duration, p.durationUnit || 'days') === maxDays
      );

      // Format the output
      if (minEntry && maxEntry) {
        const minFormatted = `${minEntry.duration} ${minEntry.durationUnit}`;
        const maxFormatted = `${maxEntry.duration} ${maxEntry.durationUnit}`;
        
        // If same unit, show as range; otherwise show both
        if (minEntry.durationUnit === maxEntry.durationUnit) {
          return `${minEntry.duration}-${maxEntry.duration} ${minEntry.durationUnit}`;
        } else {
          return `${minFormatted} - ${maxFormatted}`;
        }
      }

      return "N/A";
    };

    return (
      <div className='gig-details-right-section'>
        <Typography variant='h4' className='gig-title' fontSize={{ xs: "20px", sm: "24px", md: "28px" }}>
          {gig?.title}
        </Typography>

        <div className="seller-info">
          <Avatar alt="Freelancer" src={gig?.seller.profileImage} className="seller-avatar" />
          <a href={`/profile/${gig?.seller.buyer}`} className="seller-name">{gig?.seller.fullName}</a>
        </div>

        <hr className='separator' style={{ border: '0.2px solid #e0e0e0', margin: '16px 0' }}/>

        <div className="price-section">
          <Typography className='price-text'>
            <span className='price-amount'>₹{gig?.minPrice}</span>
            <span className='price-onwards'> onwards</span>
          </Typography>
          <a href='#pricing' className='price-table-link' onClick={scrollToPricingTab}>
            {pageTextConfig.GigDetailsRightSection.seePriceTableText} →
          </a>
        </div>

        <div className="action-buttons">
          <GGLoaderButton
            disabled={!gig?.isListed}
            isLoading={proceedBtnLoader}
            fullWidth
            size='large'
            variant='contained'
            className='proceed-button'
            startIcon={<ChatIcon />}
            onClick={createChatRoom}
          >
            {pageTextConfig.GigDetailsRightSection.proceedBtnText}
          </GGLoaderButton>

          <GGLoaderButton
            disabled={!gig?.isListed && !isGigInFavourites}
            isLoading={addToFavouritesBtnLoader}
            startIcon={isGigInFavourites ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            fullWidth
            size='large'
            variant='outlined'
            className={isGigInFavourites ? 'added-to-fav-button' : 'add-to-fav-button'}
            colorHex='#bcbcbc'
            onClick={handleFavouritesToggle}
          >
            {isGigInFavourites ? pageTextConfig.GigDetailsRightSection.addedToFavouritesBtnText : pageTextConfig.GigDetailsRightSection.addToFavouritesBtnText}
          </GGLoaderButton>
        </div>

        <div className="key-features">
          <div className="feature-item">
            <div className="feature-icon delivery">
              <AccessTimeIcon />
            </div>
            <div className="feature-text">
              <div className="feature-value">{getDeliveryTimeRange()}</div>
              <div className="feature-label">Delivery</div>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon price-bands">
              <CurrencyRupeeIcon />
            </div>
            <div className="feature-text">
              <div className="feature-value">{gig?.priceTable?.length || 0} Price Bands</div>
              <div className="feature-label">Available</div>
            </div>
          </div>

          <div className="feature-item">
            <div className="feature-icon reviews">
              {gig?.noOfRatings && gig.noOfRatings > 0 ? <StarIcon /> : <StarBorderIcon />}
            </div>
            <div className="feature-text">
              <div className="feature-value">{gig?.noOfRatings || 0} Reviews</div>
              <div className="feature-label">Rating</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const GigDescriptionSection = () => {
    return (
      <div className='gig-description-section'>
        <Typography variant='h4' fontSize={{ xs: "22px", md: "26px" }} fontWeight={700} className='section-heading' mb="16px">
          {pageTextConfig.descriptionSection.descriptionTitle}
        </Typography>
        <Typography variant='body1' fontSize={{ xs: "13px", md: "15px" }} textAlign={"justify"} sx={{ whiteSpace: 'pre-wrap' }}>
          {gig?.description}
        </Typography>
      </div>
    )
  }

  const PriceTableSection = () => {
    return (
      <div className='gig-pricing-section' id='pricing'>
        <Typography variant='h4' fontSize={{ xs: "22px", md: "26px" }} fontWeight={700} className='section-heading' mb="16px">
          {pageTextConfig.priceTableSection.priceTableTitle}
        </Typography>
        <Typography variant='subtitle1' fontSize={{ xs: "13px", md: "15px" }} className="pricing-description" mb="20px">
        These are the estimated prices based on the complexity of the project and the time required to complete it. For actual prices, please <strong><u style={{ cursor: 'pointer' }} onClick={createChatRoom}>Proceed to Chat</u></strong>
        </Typography>

        <div className="price-bands-container">
          {gig?.priceTable.map((priceItem, index) => (
            <Card key={index} className="price-band-card">
              <CardContent>
                <div className="price-band-header">
                  <span className="price-band-label">Price Band {index + 1}</span>
                  <div className="price-band-duration">~
                    <AccessTimeIcon className="duration-icon" />
                    <span>{priceItem.duration} {priceItem.durationUnit}</span>
                  </div>
                </div>
                <Typography className="price-band-price" variant="h6" component="div">
                  ₹ {priceItem.minPrice} - {priceItem.maxPrice}
                </Typography>
                <Typography variant="body2" className="price-band-description" textAlign={"justify"} fontSize="13px" sx={{ whiteSpace: 'pre-wrap' }}>
                  {priceItem.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      {showLoader && <GGLoaderView />}
      <Navbar useScrollVersion={true} />
      <div className='gig-info-page'>
        {
          gig === undefined ?
            <GGLoaderView /> :
            <>
              {gig && !gig.isListed && (
                <div className="gig-not-listed-banner">
                  This gig is currently not listed or not accepting orders by the freelancer.
                </div>
              )}
              
              <div className="gig-main-content">
                <Grid container spacing={{ xs: 2, md: 4 }}>
                  <Grid item xs={12} md={6}>
                    {gig && (
                      <GigImagesSection
                        gig={gig}
                        mainImageURL={mainImageURL}
                        setMainImageURL={setMainImageURL}
                        setImageModalIndex={setImageModalIndex}
                        setImageModalOpen={setImageModalOpen}
                        thumbnailScrollRef={thumbnailScrollRef}
                        handleThumbnailClick={handleThumbnailClick}
                        handleScrollLeft={handleScrollLeft}
                        handleScrollRight={handleScrollRight}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <GigDetailsRightSection />
                  </Grid>
                </Grid>
              </div>

              <div className="gig-tabs-section" ref={tabsSectionRef}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={activeTab} onChange={handleTabChange} aria-label="gig information tabs">
                    <Tab label="Description" />
                    <Tab label="Pricing" />
                    <Tab label="Reviews" />
                  </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                  <GigDescriptionSection />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <PriceTableSection />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  <GigReviewsSection gigId={gig.id} totalGigRatings={gig.noOfRatings} />
                </TabPanel>
              </div>
            </>
        }

      </div>

        <div className="similar-gigs-section-container">
            {gig && <SimilarGigsSection subcategoryId={gig.subcategory} gigIdToExclude={gig.id} />}
        </div>
      <Footer />
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />

      {gig && (
        <ImageListModal
          images={gig.gigImgs.map(img => img.url)}
          initialIndex={imageModalIndex}
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      )}
    </>
  )
}

export default GigInfoPage

